import { StyleDeclarationValue, css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ContentCalendarSuggestionAccountAge, ContentCalendarSuggestionAll, Industry } from '../../../extViewmodels';
import { getIndustryName } from '../../../models/UiUtils';
import { CreateGhostCalendarSuggestionViewModel } from '../../../viewmodels/AdminViewModels';
import { Checkbox } from '../../../web/components/Checkbox';
import { ISelectOption, MultiSelect } from '../../../web/components/DeprecatedSelect';
import {
	GhostCalendarAllIndustriesOption,
	GhostCalendarIndustryOptions,
} from '../../containers/templates/GhostCalendar/models';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	createSuggestion: CreateGhostCalendarSuggestionViewModel;
	styles?: StyleDeclarationValue[];
}

export const GhostCalendarSubverticals: React.FC<IProps> = observer(props => {
	const { className, createSuggestion, styles = [] } = props;

	const getSelectedIndustries = React.useMemo(() => {
		return createSuggestion?.industries?.map(x => {
			const name = getIndustryName(x as Industry);
			return {
				dataContext: x,
				id: `industry-option-${name}`,
				text: name,
			};
		});
	}, [createSuggestion?.industries]);

	const getSelectedSubverticals = React.useMemo(() => {
		return createSuggestion?.subverticals?.map(x => {
			return {
				dataContext: x,
				id: `subvertical-option-${x}`,
				text: x,
			};
		});
	}, [createSuggestion?.subverticals]);

	const onIndustryOptionClicked = React.useCallback(
		(option: ISelectOption<Industry>, wasSelected: boolean) => {
			const nextSelectedIndustries = [...(createSuggestion?.industries || [])];
			const index = nextSelectedIndustries.findIndex(x => x === option.dataContext);
			if (!!wasSelected && index < 0) {
				nextSelectedIndustries.push(option.dataContext);
			} else if (!wasSelected && index >= 0) {
				nextSelectedIndustries.splice(index, 1);
			}

			createSuggestion.industries = nextSelectedIndustries;
			createSuggestion.subverticals = [];
		},
		[createSuggestion]
	);

	const onAllIndustriesClicked = () => {
		createSuggestion.industries = createSuggestion?.isAllIndustriesSelected
			? []
			: GhostCalendarIndustryOptions.map(x => x.dataContext);
		createSuggestion.subverticals = createSuggestion?.isAllIndustriesSelected ? [] : [ContentCalendarSuggestionAll];
	};

	const onRenderIndustriesFilterTitle = React.useCallback(() => {
		const count = createSuggestion?.industries?.length || 0;
		let value = `${count} industries`;
		if (count === GhostCalendarIndustryOptions.length) {
			value = 'All industries';
		}
		if (count === 1) {
			value = createSuggestion.industries[0];
		}
		return (
			<div className={css(styleSheet.triggerContainer)}>
				<span className={css(styleSheet.triggerLabel)}>VERTICAL</span>
				<span className={css(styleSheet.triggerValue)}>{value}</span>
			</div>
		);
	}, [createSuggestion?.industries]);

	const onSelectAllSubverticals = (e: React.ChangeEvent<HTMLInputElement>) => {
		createSuggestion.subverticals = e.target.checked ? createSuggestion?.subverticalOptions : [];
	};

	const isAllSubverticalsSelected = React.useMemo(() => {
		return (
			createSuggestion?.industries?.length !== 1 ||
			createSuggestion?.subverticals?.length === createSuggestion?.subverticalOptions?.length
		);
	}, [createSuggestion?.industries, createSuggestion?.subverticals, createSuggestion?.subverticalOptions]);

	const onSubverticalChecked = (subvertical: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
		const nextSelectedSubverticals = [...(createSuggestion?.subverticals || [])];
		const index = nextSelectedSubverticals.findIndex(x => x === subvertical);
		const wasSelected = e.target.checked;
		if (!!wasSelected && index < 0) {
			nextSelectedSubverticals.push(subvertical);
		} else if (!wasSelected && index >= 0) {
			nextSelectedSubverticals.splice(index, 1);
		}

		createSuggestion.subverticals = nextSelectedSubverticals;
	};

	const onChangeCustomers =
		(value: ContentCalendarSuggestionAccountAge) => (e: React.ChangeEvent<HTMLInputElement>) => {
			const nextCustomers = [...(createSuggestion?.accountAges || [])];
			const index = nextCustomers.findIndex(x => x === value);
			const wasSelected = e.target.checked;
			if (!!wasSelected && index < 0) {
				nextCustomers.push(value);
			} else if (!wasSelected && index >= 0) {
				nextCustomers.splice(index, 1);
			}
			createSuggestion.accountAges = nextCustomers;
		};

	return (
		<div className={`${css(styleSheet.container, ...styles)} ${className || ''}`}>
			<MultiSelect
				onOptionClick={onIndustryOptionClicked}
				options={GhostCalendarIndustryOptions}
				selectedOptions={getSelectedIndustries}
				selectedOptionsTitle={onRenderIndustriesFilterTitle}
				styles={[styleSheet.industrySelect]}
				onAllClicked={onAllIndustriesClicked}
				isAllSelected={createSuggestion?.isAllIndustriesSelected}
				selectAllOption={GhostCalendarAllIndustriesOption}
			/>
			<div className={css(styleSheet.label)}>Select Subverticals this campaign will be visible for:</div>
			<div className={css(styleSheet.checkboxContainer)}>
				<Checkbox
					id='subvertical-all-checkbox'
					type='large'
					onChange={onSelectAllSubverticals}
					checked={isAllSubverticalsSelected}
					disabled={createSuggestion?.industries?.length !== 1}
					className={css(styleSheet.checkbox)}
				>
					<div className={css(styleSheet.checkboxText)}>Select all</div>
				</Checkbox>
				{createSuggestion?.subverticalOptions?.map(x => {
					return (
						<Checkbox
							key={`subvertical-${x}-checkbox`}
							id={`subvertical-${x}-checkbox`}
							type='large'
							onChange={onSubverticalChecked(x)}
							checked={isAllSubverticalsSelected || !!getSelectedSubverticals.find(y => x === y.dataContext)}
							className={css(styleSheet.checkbox)}
						>
							<div className={css(styleSheet.checkboxText)}>{x}</div>
						</Checkbox>
					);
				})}
				<div className={css(styleSheet.label)}>Show campaign for which Levitate customers?</div>
				<Checkbox
					id='customers-new-checkbox'
					type='large'
					onChange={onChangeCustomers(ContentCalendarSuggestionAccountAge.New)}
					checked={createSuggestion?.accountAges?.includes(ContentCalendarSuggestionAccountAge.New)}
					className={css(styleSheet.checkbox)}
				>
					<div className={css(styleSheet.checkboxText)}>New customers</div>
				</Checkbox>
				<Checkbox
					id='customers-existing-checkbox'
					type='large'
					onChange={onChangeCustomers(ContentCalendarSuggestionAccountAge.Existing)}
					checked={createSuggestion?.accountAges?.includes(ContentCalendarSuggestionAccountAge.Existing)}
					className={css(styleSheet.checkbox)}
				>
					<div className={css(styleSheet.checkboxText)}>Existing customers</div>
				</Checkbox>
			</div>
		</div>
	);
});
